import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { Modal } from '@spirit-testing/component-library';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TestStepService } from '../../../services/test-step.service';
import { StepCategory, TestStep } from '../../../models/test-case.model';

type CategoryFilter = StepCategory | 'ALL';

const CATEGORY_FILTERS: readonly CategoryFilter[] = ['ALL', 'SETUP', 'AUTHENTICATION', 'VERIFICATION', 'TEARDOWN'];

interface LibraryStepRow {
  id: string;
  shortId: string;
  label: string;
  secondary: string | null;
  category: StepCategory | null;
  categoryLabel: string;
  categoryClass: string;
  usageCount: number;
}

/**
 * Bibliothek-Dialog (NEX-537, Design app-modals.jsx:1083-1197 TestschrittBibliothekModal):
 * listet die wiederverwendbaren Bibliotheks-Schritte des Projekts mit Kennung,
 * Typ-Pill und Verwendungs-Counter, erlaubt Suche, Typ-Filter und Mehrfachauswahl
 * und gibt die gewaehlten Schritt-IDs via {@link applied} an den aufrufenden Kontext
 * (TestStepTableComponent) zur Copy-on-use-Uebernahme. Schritte duerfen mehrfach als
 * Kopie hinzugefuegt werden.
 */
@Component({
  selector: 'app-test-step-library-dialog',
  imports: [Modal, TranslatePipe, MatCheckbox, MatIcon, MatProgressSpinner],
  templateUrl: './test-step-library-dialog.component.html',
  styleUrl: './test-step-library-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestStepLibraryDialogComponent implements AfterViewInit {
  private readonly stepService = inject(TestStepService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private loaded = false;

  private readonly currentLang = toSignal(this.translate.onLangChange.pipe(map((e) => e.lang)), {
    initialValue: this.translate.currentLang,
  });

  readonly projectId = input.required<string>();
  readonly visible = model(false);

  readonly applied = output<string[]>();

  @ViewChild('libraryContent') libraryContentTemplate!: TemplateRef<unknown>;
  @ViewChildren('rowRef') rowElements!: QueryList<ElementRef<HTMLElement>>;
  resolvedTemplate: TemplateRef<unknown> | null = null;

  readonly steps = signal<TestStep[]>([]);
  readonly loading = signal(true);
  readonly error = signal(false);
  readonly searchTerm = signal('');
  readonly selectedCategory = signal<CategoryFilter>('ALL');
  readonly selectedIds = signal<ReadonlySet<string>>(new Set());
  /** Roving-Tabindex: nur diese Zeile ist Tab-Stopp; Pfeiltasten verschieben sie. */
  readonly activeIndex = signal(0);

  readonly categoryFilters = CATEGORY_FILTERS;

  readonly filtered = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    const category = this.selectedCategory();
    return this.steps().filter((s) => {
      if (category !== 'ALL' && (s.category ?? null) !== category) {
        return false;
      }
      if (!term) {
        return true;
      }
      return (
        (s.title ?? '').toLowerCase().includes(term) ||
        (s.action ?? '').toLowerCase().includes(term) ||
        (s.notes ?? '').toLowerCase().includes(term) ||
        `#${s.id.slice(0, 8)}`.includes(term)
      );
    });
  });

  readonly rows = computed<LibraryStepRow[]>(() => {
    // onLangChange is not a signal; reading currentLang() re-runs this computed
    // so the translated category labels follow a language switch.
    this.currentLang();
    return this.filtered().map((s) => this.toRow(s));
  });

  readonly selectedCount = computed(() => this.selectedIds().size);
  readonly canApply = computed(() => this.selectedIds().size > 0);

  readonly saveLabel = computed(() => {
    // See rows(): touch currentLang() so the label re-translates on switch.
    this.currentLang();
    const count = this.selectedCount();
    return count > 0
      ? this.translate.instant('testCases.form.testSteps.libraryDialog.applyCount', { count })
      : this.translate.instant('testCases.form.testSteps.libraryDialog.apply');
  });

  constructor() {
    // Schritte erst beim ersten Öffnen laden, nicht schon beim Rendern der Testfall-Form.
    effect(() => {
      if (this.visible() && !this.loaded) {
        this.loaded = true;
        this.loadSteps();
      }
    });
  }

  ngAfterViewInit(): void {
    this.resolvedTemplate = this.libraryContentTemplate;
    this.cdr.detectChanges();
  }

  filterLabel(filter: CategoryFilter): string {
    return filter === 'ALL'
      ? this.translate.instant('testCases.form.testSteps.libraryDialog.filterAll')
      : this.translate.instant(`testSteps.category.${filter}`);
  }

  isActiveFilter(filter: CategoryFilter): boolean {
    return this.selectedCategory() === filter;
  }

  setFilter(filter: CategoryFilter): void {
    this.selectedCategory.set(filter);
    // Gefilterte Liste wechselt -> aktiven Tab-Stopp auf den ersten Treffer setzen,
    // sonst zeigte der Roving-Tabindex ins Leere.
    this.activeIndex.set(0);
  }

  onSearch(event: Event): void {
    this.searchTerm.set((event.target as HTMLInputElement).value);
    this.activeIndex.set(0);
  }

  isSelected(stepId: string): boolean {
    return this.selectedIds().has(stepId);
  }

  toggle(stepId: string): void {
    this.selectedIds.update((current) => {
      const next = new Set(current);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  }

  /**
   * Roving-Tabindex-Navigation auf der Listbox: Pfeiltasten/Home/End verschieben den
   * Fokus zwischen den Zeilen (ein Tab-Stopp für die ganze Liste), Enter/Space toggelt
   * die aktive Zeile. Nicht behandelte Tasten (u. a. Tab) bleiben unangetastet, damit
   * Tab die Liste als Ganzes verlässt und zum „Übernehmen"-Button springt.
   */
  onListKeydown(event: KeyboardEvent): void {
    const last = this.rows().length - 1;
    if (last < 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        this.moveActive(Math.min(this.activeIndex() + 1, last), event);
        break;
      case 'ArrowUp':
        this.moveActive(Math.max(this.activeIndex() - 1, 0), event);
        break;
      case 'Home':
        this.moveActive(0, event);
        break;
      case 'End':
        this.moveActive(last, event);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.toggleActive();
        break;
    }
  }

  onRowClick(index: number, stepId: string): void {
    this.activeIndex.set(index);
    this.toggle(stepId);
  }

  private moveActive(index: number, event: KeyboardEvent): void {
    event.preventDefault();
    this.activeIndex.set(index);
    this.focusActiveRow();
  }

  private toggleActive(): void {
    const row = this.rows()[this.activeIndex()];
    if (row) {
      this.toggle(row.id);
    }
  }

  private focusActiveRow(): void {
    this.rowElements?.get(this.activeIndex())?.nativeElement.focus();
  }

  onApply(): void {
    if (!this.canApply()) {
      return;
    }
    this.applied.emit([...this.selectedIds()]);
    this.close();
  }

  onCancel(): void {
    this.close();
  }

  private close(): void {
    this.visible.set(false);
    this.searchTerm.set('');
    this.selectedCategory.set('ALL');
    this.selectedIds.set(new Set());
    this.activeIndex.set(0);
  }

  private toRow(step: TestStep): LibraryStepRow {
    const category = step.category ?? null;
    return {
      id: step.id,
      // Stand-in wie in der Bibliotheks-Übersicht: das Modell hat (noch) keinen
      // menschenlesbaren Key wie "TS-12", daher die ersten 8 UUID-Zeichen.
      shortId: `#${step.id.slice(0, 8)}`,
      label: (step.title ?? '').trim() || (step.action ?? '').trim(),
      secondary: step.expectedResult?.trim() || null,
      category,
      categoryLabel: category ? this.translate.instant(`testSteps.category.${category}`) : '',
      categoryClass: category ? `tsl__cat tsl__cat--${category.toLowerCase()}` : '',
      usageCount: step.usageCount ?? 0,
    };
  }

  private loadSteps(): void {
    this.loading.set(true);
    this.error.set(false);
    this.stepService
      .listLibrary(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (steps) => {
          this.steps.set(steps);
          this.loading.set(false);
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }
}
